/* eslint-disable react/jsx-no-bind */
/* eslint-disable react/jsx-no-useless-fragment */
import React, { useCallback, useEffect, useState, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import dayjs from 'dayjs-ext';
import Highcharts from 'helpers/highcharts-setup';
import AddHighmapsModule from 'highcharts/modules/map';
import { useTranslation } from 'react-i18next';
import {
  withHighcharts,
  CandlestickSeries,
  Chart,
  ColumnSeries,
  HighchartsStockChart,
  XAxis,
  YAxis,
  Tooltip
} from 'react-jsx-highstock';
import {
  roundToPrevTick,
  roundToNearestTick,
  getChartRange,
  getChartMinTickInterval,
  getChartStartDate
} from 'helpers/dates';
import { formatAmount } from '@nbx/frontend-helpers/format';
import { comparePropsIgnoringFunctions } from 'helpers/memo';
import { NBXMarkets } from 'services';
import Text from 'components/shared/Text';
import Spinner from 'components/shared/Spinner';
import styles from './styles.module.scss';

AddHighmapsModule(Highcharts);

// Functions called in Highcharts scope can't access state, so these have to live outside of React
let mapZoomHandler = null;
let zoomTimeout = null;
let isZooming = false;
function CandlestickChart(props) {
  const [loading, setLoading] = useState(true);
  const chartRef = useRef(null);
  const didCancel = useRef(false);
  const isInitialLoad = useRef(true);
  const refreshInterval = useRef();
  const haveZoomed = useRef(false);
  const canDblClick = useRef(false);
  const minTickInterval = useRef(getChartMinTickInterval(props.frequency));
  const chartRange = useRef(getChartRange(props.frequency));
  const { t } = useTranslation();
  const [showVolumeSticks, setShowVolumeSticks] = useState(false);
  const dataRef = useRef({
    sticks: [],
    volume: []
  });

  Highcharts.Tooltip.prototype.applyFilter = () => {};

  const fetchChartData = useCallback(
    async () => {
      const realStart = isInitialLoad.current
        ? props.start
        : dayjs(roundToPrevTick(dayjs(new Date()).valueOf(), minTickInterval.current)).toISOString();

      const updateChart = candlesticks => {
        if (didCancel.current) return;
        const startAsMS = dayjs(realStart).valueOf();
        candlesticks = candlesticks.filter(item => item[0] * 1000 >= startAsMS);
        if (isInitialLoad.current) {
          setInitialCandlesticks(candlesticks);
        } else {
          updateCandlesticks(candlesticks);
        }
        if (loading) {
          setLoading(false);
        }
      };

      await NBXMarkets.getAndRenderCandlesticksForCharts(
        props.pair,
        props.frequency,
        realStart,
        dayjs(),
        isInitialLoad.current ? updateChart : undefined,
        didCancel
      ).then(candlesticks => {
        let isPanning = false;
        if (chartRef.current && chartRef.current.pointer && !!chartRef.current.pointer.hasDragged)
          isPanning = true;
        if (didCancel.current || isZooming || isPanning) {
          console.log('user is zooming or panning, abandoning update'); // eslint-disable-line
          return;
        }

        if (isInitialLoad.current) {
          isInitialLoad.current = false;
          setTimeout(() => {
            if (didCancel.current) return;
            canDblClick.current = true;
            setShowVolumeSticks(true);
          }, 1000);
        } else {
          updateChart(candlesticks);
        }
      });
    },
    [] // eslint-disable-line
  );

  useEffect(() => {
    didCancel.current = false;
    if (refreshInterval.current) {
      clearInterval(refreshInterval.current);
    }
    fetchChartData().then(() => {
      if (!didCancel.current) {
        refreshInterval.current = setInterval(() => fetchChartData(), props.refreshRate);
      }
    });
    return () => {
      didCancel.current = true;
      clearInterval(refreshInterval.current);
    };
  }, [fetchChartData, props.refreshRate, props.start]);

  function setInitialCandlesticks(candlesticks) {
    const sticks = [];
    const volume = [];

    candlesticks.forEach(item => {
      const date = item[0] * 1000;
      sticks.push([
        date,
        item[2], // open
        item[3], // high
        item[4], // low
        item[5] // close
      ]);

      volume.push([
        date,
        item[1] // volume
      ]);
    });

    dataRef.current = { sticks, volume };
    if (chartRef.current?.xAxis?.[0]?.series?.[0] && chartRef.current?.xAxis?.[0]?.series?.[1]) {
      chartRef.current.xAxis[0].series[0].setData(sticks);
      chartRef.current.xAxis[0].series[1].setData(volume);
    }
  }

  function adjustOpen(item, sticks, subtraction) {
    const prevPoint = sticks[sticks.length - subtraction];
    // prevPoint[4] is closing price, since it is in the order mapped for highcharts
    // Make sure that opening of this stick and the closing of the last one line up
    if (prevPoint[4] < item[2]) {
      // If the price rose, adjust opening, check if we need to adjust low point for stick
      item[2] = prevPoint[4]; // eslint-disable-line
      if (item[4] > prevPoint[4]) item[4] = prevPoint[4]; // eslint-disable-line
    } else if (prevPoint[4] > item[2]) {
      // If the price fell, adjust opening, check if we need to adjust high point for stick
      item[2] = prevPoint[4]; // eslint-disable-line
      if (item[3] < prevPoint[4]) item[3] = prevPoint[4]; // eslint-disable-line
    }
    return item;
  }

  function updateCandlesticks(candlesticks) {
    const dataObj = { sticks: [...dataRef.current.sticks], volume: [...dataRef.current.volume] };
    const currentLatestStick = dataObj.sticks[dataObj.sticks.length - 1];

    if (!chartRef?.current?.xAxis?.[0]) return;
    let minX = chartRef.current.xAxis[0].min;
    let maxX = chartRef.current.xAxis[0].max;
    // start will move forward every tick
    const startAsMS = getChartStartDate(props.frequency, true).valueOf();

    const latestPossibleTick = roundToPrevTick(dayjs(new Date()).valueOf(), minTickInterval.current);
    if (candlesticks.length === 0 && !(currentLatestStick && currentLatestStick[0] < latestPossibleTick)) {
      return;
    }

    // If enough time has passed while the user was on the page that we need a new candlestick
    // but the backend hasn't sent new data, we add it with current data
    // extending closing price of last candlestick into a new candlestick
    if (currentLatestStick && currentLatestStick[0] < latestPossibleTick) {
      const newLatestStick = [
        latestPossibleTick,
        currentLatestStick[4],
        currentLatestStick[4],
        currentLatestStick[4],
        currentLatestStick[4]
      ];
      dataObj.sticks.push(newLatestStick);
      if (chartRef.current) {
        // adjust the chart range to move up one tick
        minX += minTickInterval.current;
        maxX += minTickInterval.current;
        // remove data that is now out of range
        if (dataObj.sticks[0][0] < startAsMS) dataObj.sticks.shift();
        if (dataObj.volume.length > 0 && dataObj.volume[0][0] < startAsMS) dataObj.volume.shift();
      }
    } else if (currentLatestStick) {
      // Parse the latest candlestick received from the backend
      const date = candlesticks[0][0] * 1000;
      const stickAlreadyExists = date === currentLatestStick[0];
      const stickIsNew = date > currentLatestStick[0];

      if (stickAlreadyExists) {
        candlesticks[0] = adjustOpen(candlesticks[0], dataObj.sticks, 2);
      }
      if (stickIsNew) {
        candlesticks[0] = adjustOpen(candlesticks[0], dataObj.sticks, 1);
      }

      const newLatestStick = [
        date,
        candlesticks[0][2], // open
        candlesticks[0][3], // high
        candlesticks[0][4], // low
        candlesticks[0][5] // close
      ];
      const newLatestVolume = [
        date,
        candlesticks[0][1] // volume
      ];

      // If the latest candlestick is for the same time as one we already have, update that data
      if (stickAlreadyExists) {
        dataObj.sticks[dataObj.sticks.length - 1] = newLatestStick;
        if (dataObj.volume.length > 0) dataObj.volume[dataObj.volume.length - 1] = newLatestVolume;
        else dataObj.volume = [newLatestVolume];

        // If the latest candlestick represents a time entry beyond what we already have, append it
      } else if (stickIsNew) {
        dataObj.sticks.push(newLatestStick);
        dataObj.volume.push(newLatestVolume);
        if (chartRef.current) {
          // adjust the chart range to move up one tick
          minX += minTickInterval.current;
          maxX += minTickInterval.current;
          // remove data that is now out of range
          if (dataObj.sticks[0][0] < startAsMS) dataObj.sticks.shift();
          if (dataObj.volume[0][0] < startAsMS) dataObj.volume.shift();
        }
      }
    }

    if (chartRef.current) {
      // If tab is in the background, chartRef.current seems to be null sometimes
      try {
        chartRef.current.xAxis[0].series[0].setData(dataObj.sticks);
        chartRef.current.xAxis[0].series[1].setData(dataObj.volume);
        // set new chart range
        chartRef.current.xAxis[0].update({ min: minX, max: maxX });
        scaleYAxisAfterUpdate(dataObj.sticks, minX, maxX);
      } catch (error) {} // eslint-disable-line
    }
    dataRef.current = dataObj;
  }

  // These functions are called in highchart's context, so this refers to the chart and not the component
  /* eslint-disable react/no-this-in-sfc */
  function formatPriceLabel() {
    return formatAmount(this.value, props.market.minTickLength);
  }

  function positionTooltip() {
    return {
      x: this.chart.plotLeft - 5,
      y: this.chart.plotTop - 33
    };
  }

  function formatTooltip() {
    let { x } = this;
    if (!Number.isInteger(x / minTickInterval.current)) x = roundToNearestTick(x, minTickInterval.current);
    const { xData } = this.points[0].series.chart.xAxis[0].series[0];
    const point = this.points[0].series.chart.xAxis[0].series[0].yData.find((_, index) => xData[index] === x);
    const open = `<span class="tooltip-title">O: </span><span class="tooltip-figure">${formatAmount(
      point[0],
      props.market.minTickLength
    )} </span>`;
    const high = `<span class="tooltip-title">H: </span><span class="tooltip-figure">${formatAmount(
      point[1],
      props.market.minTickLength
    )} </span>`;
    const low = `<span class="tooltip-title">L: </span><span class="tooltip-figure">${formatAmount(
      point[2],
      props.market.minTickLength
    )} </span>`;
    const close = `<span class="tooltip-title">C: </span><span class="tooltip-figure">${formatAmount(
      point[3],
      props.market.minTickLength
    )} </span>`;
    return `<div>${open} ${high} ${low} ${close}</div>`;
  }

  function scaleYAxisAfterPan(e) {
    if (e.trigger !== 'pan') return;
    if (!haveZoomed.current) return;
    const minX = e.min;
    const maxX = e.max;
    let minY = Number.MAX_SAFE_INTEGER;
    let maxY = 0;
    const { xData } = this.chart.xAxis[0].series[0];
    this.chart.xAxis[0].series[0].yData
      .filter((_, index) => xData[index] <= maxX && xData[index] >= minX)
      .forEach(stick => {
        for (let i = 0; i < 4; i++) {
          if (stick[i] < minY) minY = stick[i];
          if (stick[i] > maxY) maxY = stick[i];
        }
      });
    chartRef.current.yAxis[0].update({ min: minY, max: maxY });
  }
  /* eslint-enable */

  function scaleYAxisAfterUpdate(sticks, minX, maxX) {
    if (haveZoomed.current) {
      let minY = Number.MAX_SAFE_INTEGER;
      let maxY = 0;
      sticks
        .filter(stick => stick[0] <= maxX && stick[0] >= minX)
        .forEach(stick => {
          for (let i = 1; i <= 4; i++) {
            if (stick[i] < minY) minY = stick[i];
            if (stick[i] > maxY) maxY = stick[i];
          }
        });
      chartRef.current.yAxis[0].update({ min: minY, max: maxY });
    }
  }

  const handleMouseWheel = e => {
    if (isInitialLoad.current) return; // don't allow to zoom while initial load is happening.
    if (zoomTimeout) clearTimeout(zoomTimeout);
    isZooming = true;
    const pseudoEvent = {
      chartX: chartRef.current.plotWidth / 2,
      chartY: chartRef.current.plotHeight / 2,
      wheelDelta: e.wheelDelta,
      detail: e.detail
    };
    mapZoomHandler(pseudoEvent);
    zoomTimeout = setTimeout(() => {
      isZooming = false;
      if (!haveZoomed.current) haveZoomed.current = true;
    }, 500);
  };

  const customZoom = e => {
    const chart = chartRef.current;
    const sensitivityMultiplier = e.detail || -(e.wheelDelta / 120);
    chart.mapZoom(chart.options.mapNavigation.mouseWheelSensitivity ** sensitivityMultiplier);
    chart.xAxis[0].toValue(chart.chartX);
    chart.yAxis[0].toValue(chart.chartY);
  };

  const setChartRef = chart => {
    chartRef.current = chart;
    // mapZoomHandler = chart.pointer.onContainerMouseWheel.bind(chart.pointer);
    mapZoomHandler = customZoom;

    chart.pointer.onContainerMouseWheel = handleMouseWheel;
    const originalDblClick = chart.pointer.onContainerDblClick.bind(chart.pointer); // clone function;
    chart.pointer.onContainerDblClick = e => {
      if (canDblClick.current) {
        originalDblClick(e);
      }
    };
    Highcharts.addEvent(chart.container, 'mousewheel', handleMouseWheel);
    const extremesInterval = setInterval(() => {
      if (!chart || !chart.xAxis || chart.xAxis.length === 0 || !chart.xAxis[0]) return;
      clearInterval(extremesInterval);
      const now = roundToPrevTick(dayjs(new Date()).valueOf(), minTickInterval.current);
      const minX = now - chartRange.current;
      const maxX = now;
      chart.xAxis[0].update({ min: minX, max: maxX });
    }, 50);
  };

  if (loading) {
    return <Spinner delayed />;
  }

  return (
    <div className={classnames([styles.fadeinWrap, 'trade-chart-cs'])}>
      <HighchartsStockChart
        styledMode
        time={{ useUTC: false }}
        mapNavigation={{
          enabled: !props.isMobile,
          enableTouchZoom: false,
          enableButtons: false,
          enableDoubleClickZoom: false
        }}
        plotOptions={{
          tooltip: {
            followTouchMove: false
          }
        }}
        callback={setChartRef}>
        <Chart
          animation={!isInitialLoad.current}
          spacingTop={30}
          panning={!isInitialLoad.current}
          pinchType="x"
        />
        <XAxis
          type="datetime"
          minRange={minTickInterval.current * 7}
          minTickInterval={minTickInterval.current}
          tickPixelInterval={150}
          labels={{
            autoRotation: false
          }}
          startOnTick={false}
          endOnTick={false}
          crosshair
          // eslint-disable-next-line react/jsx-no-bind
          onSetExtremes={scaleYAxisAfterPan}
        />
        <YAxis
          opposite
          labels={{
            align: 'left',
            formatter: formatPriceLabel
          }}
          height="90%"
          top="0%"
          crosshair
          className="candlesticks">
          <CandlestickSeries data={dataRef.current.sticks} softThreshold />
        </YAxis>
        <YAxis height="30%" top="70%" min={0} labels={{ enabled: false }}>
          <ColumnSeries
            visible={showVolumeSticks}
            data={dataRef.current.volume}
            enableMouseTracking={false}
          />
        </YAxis>
        <Tooltip
          // eslint-disable-next-line react/jsx-no-bind
          positioner={positionTooltip}
          // eslint-disable-next-line react/jsx-no-bind
          formatter={formatTooltip}
          shared
          split={false}
          followTouchMove={false}
        />
      </HighchartsStockChart>
      {props.isMobile && (
        <Text muted styles={[styles.swipeTip]}>
          {t('market_swipe_the_chart')}
        </Text>
      )}
    </div>
  );
}

CandlestickChart.propTypes = {
  pair: PropTypes.string.isRequired,
  refreshRate: PropTypes.number.isRequired,
  frequency: PropTypes.string.isRequired,
  start: PropTypes.string.isRequired,
  market: PropTypes.object,
  isMobile: PropTypes.bool
};

CandlestickChart.defaultProps = {
  market: null,
  isMobile: false
};

export default memo(withHighcharts(CandlestickChart, Highcharts), comparePropsIgnoringFunctions);
